import { track } from '@amplitude/analytics-browser'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import isEmpty from 'lodash.isempty'
import type { PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'

import CalendarPage from 'CalendarPage'
import type {
  AccountSelectorProps,
  ConversationProps,
  UpcomingPostCardProps,
  PostPreviewSectionProps,
  PostActionProps,
} from 'Components'
import {
  Conversation,
  Navbar,
  LoadingPlaceholder,
  PostAction,
  UpcomingPostCard,
  PostPreviewSection,
  EmptyUpcomingPosts,
  NoPostToApprove,
} from 'Components'
import type { NavbarTabView, TabViewValue } from 'Components/Navbar'
import InstagramGridPage from 'InstagramGridPage'
import { datePlaceholderForDraftPost } from 'normalizer'
import { Button } from 'ui/button'
import type { PreviewVariant } from 'utils/postPreviewVariant'

// SKE-3789: layout migrated styled-components → Tailwind. Behaviour held
// constant — grid template + 1280px breakpoint, per-column scroll, the
// sticky "Upcoming Posts" header, the `hidden` visibility toggling (now
// Tailwind's `hidden` util), and the delay-show column animation
// (keyframe `custom-show-animation` lives in index.css). The 1280px
// breakpoint uses an arbitrary `max-[1280px]:`/`min-[1280px]:` literal.

// "Upcoming Posts" heading (was the styled `Title` h4: 16px/700)
const TITLE_CLASS = 'font-gotham text-xl font-extrabold text-neutral-800'

// Centre column: overflow-x stays `auto` to match prior rendered behaviour —
// the old `overflow-x: auto !important` won over the inline `visible`, so the
// "PDF tooltip" override never actually applied. overflow-y auto for long
// previews.
const LEGACY_APPROVAL_GRID_CLASS =
  'mx-auto grid grid-cols-[1fr_440px_1fr] overflow-x-auto overflow-y-hidden max-[1280px]:grid-cols-[1fr_400px_1fr]'

const UPLIFT_APPROVAL_GRID_CLASS =
  'mx-auto grid grid-cols-[minmax(480px,1fr)_minmax(480px,auto)_minmax(480px,1fr)] overflow-x-auto overflow-y-hidden max-[1280px]:grid-cols-[minmax(300px,1fr)_minmax(440px,auto)_minmax(300px,1fr)]'

const LEGACY_PREVIEW_COLUMN_CLASS =
  'relative h-[calc(100vh-70px)] overflow-auto border-x border-neutral-100 bg-white px-10 pt-6 max-[1280px]:px-5'

const UPLIFT_PREVIEW_COLUMN_CLASS =
  'relative box-border h-[calc(100vh-70px)] min-w-[480px] overflow-auto border-x border-neutral-100 bg-white px-10 pt-6 max-[1280px]:min-w-[440px] max-[1280px]:px-5'

export const getApprovalGridClassName = (previewVariant: PreviewVariant) =>
  previewVariant === 'uplift'
    ? UPLIFT_APPROVAL_GRID_CLASS
    : LEGACY_APPROVAL_GRID_CLASS

export const getPreviewColumnClassName = (previewVariant: PreviewVariant) =>
  previewVariant === 'uplift'
    ? UPLIFT_PREVIEW_COLUMN_CLASS
    : LEGACY_PREVIEW_COLUMN_CLASS

interface AppProps {
  lastAction: string | null
  isLoading: boolean
  accountSelectorProps: AccountSelectorProps
  setting: {
    logoUrl: string
    companyName: string
  }
  upcomingPosts: {
    id: string
    date: string
    isVisible: boolean
    posts: (UpcomingPostCardProps & {
      isVisible?: boolean
      postIndex: number
    })[]
  }[]
  postPreviewSectionProps: PostPreviewSectionProps
  conversationProps: ConversationProps
  postActionProps: PostActionProps
  handleCardClick: (postRowId: string, postId: string, status: string) => void
  navigatePost: (postIndex: number) => void
  isReadOnly: boolean
  previewVariant: PreviewVariant
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
}

function App({
  accountSelectorProps,
  lastAction,
  isLoading,
  setting,
  upcomingPosts,
  postPreviewSectionProps,
  conversationProps,
  postActionProps,
  handleCardClick,
  navigatePost,
  isReadOnly,
  previewVariant,
  updatePostState,
}: PropsWithChildren<AppProps>) {
  const [tabView, setTabView] = useState<TabViewValue>('approval')
  const [shouldShowConfetti, setShouldShowConfetti] = useState(false)

  const renderTotalPendingCount = (posts: AppProps['upcomingPosts']) => {
    const flattenArr = posts.map((item) => item.posts).flat()

    let pendingCount = 0
    flattenArr.forEach((item) => {
      if (item.status === 'pending') {
        pendingCount += 1
      }
    })
    return pendingCount
  }

  const handlePendingCountClick = () => {
    const firstPendingPostIndex = upcomingPosts
      .flatMap((item) => item.posts)
      .findIndex((post) => post.status === 'pending')

    navigatePost(firstPendingPostIndex + 1)
  }

  // "View Pending" jumps to the first pending post, so disable it once that
  // post is already selected — clicking would be a no-op.
  const isFirstPendingSelected =
    upcomingPosts
      .flatMap((item) => item.posts)
      .find((post) => post.status === 'pending')?.isSelected ?? false

  // Trigger confetti when the reviewer clears the queue. Idempotent —
  // setShouldShowConfetti(true) is a no-op if already true (React
  // dedupes via Object.is), so a re-render mid-show doesn't restart
  // the timer or fire a second burst. Reading shouldShowConfetti
  // inside this effect would put us in an infinite loop once we add
  // it to deps and the timer flips it back to false (lastAction is
  // never cleared, so the conditions remain true), so the trigger
  // doesn't read it at all — separation of trigger vs. timer below.
  useEffect(() => {
    if (
      lastAction &&
      renderTotalPendingCount(upcomingPosts) === 0 &&
      ['approved', 'submitChangeDetail'].includes(lastAction)
    ) {
      // Honour the OS-level reduced-motion preference for vestibular-
      // sensitive reviewers. The matchMedia check is deferred to the
      // moment we'd otherwise trigger so a preference change between
      // mount and "all approved" is picked up.
      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      document.getElementById('post-list-column')?.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
      if (!prefersReducedMotion) {
        setShouldShowConfetti(true)
      }
    }
  }, [upcomingPosts, lastAction])

  // Auto-hide confetti 5 s after it appears. Owning the timer in its
  // own effect means an unrelated re-render mid-show (e.g.
  // upcomingPosts changing while confetti is up) doesn't cancel the
  // timer the way the previous combined effect did — that race left
  // confetti stuck on screen indefinitely. Cleanup runs only when
  // shouldShowConfetti flips or the component unmounts.
  useEffect(() => {
    if (!shouldShowConfetti) return
    const timer = setTimeout(() => setShouldShowConfetti(false), 5000)
    return () => clearTimeout(timer)
  }, [shouldShowConfetti])

  let navbarTabView: NavbarTabView = {
    defaultValue: tabView,
    leftItem: {
      value: 'approval' as TabViewValue,
      label: 'Approvals',
    },
    middleItem: {
      value: 'calendar' as TabViewValue,
      label: 'Calendar',
    },
    rightItem: {
      value: 'instagramGrid' as TabViewValue,
      label: 'Grid Preview',
    },
    onViewChange: (view: TabViewValue) => {
      const curTabView = tabView
      setTabView(view)
      if (view !== curTabView) {
        if (view === 'approval') {
          track('ext-approval-tab-click')
        } else if (view === 'calendar') {
          track('ext-cal-tab-click', {
            isReadOnly,
          })
        } else if (view === 'instagramGrid') {
          track('ext-planner-tab-click', {
            isReadOnly,
          })
        }
      }
    },
  }

  if (isReadOnly) {
    navbarTabView = {
      defaultValue: 'calendar',
      leftItem: {
        value: 'calendar' as TabViewValue,
        label: 'Calendar',
      },
      rightItem: {
        value: 'instagramGrid' as TabViewValue,
        label: 'Grid Preview',
      },
      onViewChange: navbarTabView.onViewChange,
    }
  }

  return (
    <div>
      {shouldShowConfetti && <Confetti numberOfPieces={250} recycle={false} />}
      {!isLoading && (
        <Navbar
          logo={setting.logoUrl}
          name={setting.companyName}
          view={navbarTabView}
        />
      )}

      {tabView === 'calendar' ? (
        <CalendarPage
          setTabView={setTabView}
          isReadOnly={isReadOnly}
          previewVariant={previewVariant}
        />
      ) : tabView === 'instagramGrid' ? (
        <InstagramGridPage
          accountSelectorProps={accountSelectorProps}
          isReadOnly={isReadOnly}
        />
      ) : isLoading ? (
        <LoadingPlaceholder previewVariant={previewVariant} />
      ) : !isLoading &&
        isEmpty(upcomingPosts.flatMap((p) => p.posts)) &&
        !isReadOnly ? (
        <EmptyUpcomingPosts />
      ) : (
        !isReadOnly && (
          <div className="bg-[#fcfcfc]">
            <div className={getApprovalGridClassName(previewVariant)}>
              <div
                id="post-list-column"
                className="relative ml-auto mt-6 h-[calc(100vh-94px)] min-w-[480px] overflow-x-auto bg-[#fcfcfc] py-0 pl-14 pr-[30px] max-[1280px]:w-full max-[1280px]:min-w-[300px] max-[1280px]:px-5"
              >
                {renderTotalPendingCount(upcomingPosts) > 0 && (
                  <div className="sticky top-0 z-10 -mr-[2%] flex w-[102%] items-center pb-[10px] bg-[#fcfcfc]">
                    <div
                      className={cn(
                        TITLE_CLASS,
                        'flex w-full items-center justify-between'
                      )}
                    >
                      <span>Upcoming Posts</span>
                      <Button
                        variant="link"
                        id="pending-count"
                        onClick={handlePendingCountClick}
                        disabled={isFirstPendingSelected}
                        className="ml-[10px] h-auto shrink-0 p-0 text-sm"
                      >
                        View Pending ({renderTotalPendingCount(upcomingPosts)})
                      </Button>
                    </div>
                  </div>
                )}

                {renderTotalPendingCount(upcomingPosts) === 0 && (
                  <>
                    <h4 className={TITLE_CLASS}>Upcoming Posts</h4>
                    <div className="mb-4 mt-[10px]">
                      <NoPostToApprove />
                    </div>
                  </>
                )}

                <div id="posts-container">
                  {upcomingPosts.map(
                    ({ date, posts, id: postRowId, isVisible }) => {
                      return (
                        <div key={postRowId}>
                          <div
                            className={cn(
                              'mb-[18px] flex items-center gap-3 text-sm font-medium text-neutral-600',
                              !isVisible && 'hidden'
                            )}
                          >
                            <span className="flex shrink-0 items-center gap-2">
                              <i
                                aria-hidden="true"
                                className={cn(
                                  'text-neutral-500',
                                  date === datePlaceholderForDraftPost
                                    ? 'fa-regular fa-pen-to-square'
                                    : 'fa-regular fa-calendar'
                                )}
                              />
                              {date === datePlaceholderForDraftPost
                                ? 'Drafts'
                                : date}
                            </span>
                            <span
                              className="h-px flex-1 bg-neutral-100"
                              aria-hidden="true"
                            />
                          </div>
                          {posts.map((post) => (
                            <div
                              className={cn(
                                'mb-[18px] cursor-pointer scroll-mt-10',
                                !post.isVisible && 'hidden'
                              )}
                              key={post.id}
                              onClick={() =>
                                handleCardClick(postRowId, post.id, post.status)
                              }
                              data-postid={post.id}
                              data-postindex={post.postIndex}
                              data-post-status-pending={
                                post.status === 'pending'
                              }
                            >
                              <UpcomingPostCard
                                {...post}
                                updatePostState={updatePostState}
                              />
                            </div>
                          ))}
                        </div>
                      )
                    }
                  )}
                </div>
              </div>
              <div className={getPreviewColumnClassName(previewVariant)}>
                <PostPreviewSection {...postPreviewSectionProps} />
              </div>
              <div
                data-testid="post-action-column"
                className="relative flex h-[calc(100vh-70px)] min-w-[480px] flex-col overflow-hidden bg-white pt-6 motion-safe:[animation:custom-show-animation_500ms_ease-in_0s_0.5] max-[1280px]:min-w-[auto] max-[1280px]:pt-5"
              >
                {/* Actions stay padded + fixed; the conversation below fills the
                    remaining height and scrolls, with its divider lines bleeding
                    to the column edges (no column px so the lines touch left). */}
                <div className="shrink-0 px-10 max-[1280px]:px-5">
                  <PostAction {...postActionProps} />
                  <div className="h-6" />
                </div>
                <Conversation {...conversationProps} />
              </div>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default App
